<template>
  <div class="order-food-table-qrcode-print">
    <div ref="printPage" class="order-food-table-qrcode-print__print">
      <div
        v-for="(page, pageIdx) in pages"
        :key="pageIdx"
        class="order-food-table-qrcode-print__page"
      >
        <div class="order-food-table-qrcode-print__title">
          {{ $t('General.SmaDay') }} / {{ $t('OrderFoodEntry.PrintTableQrCode') }}
        </div>
        <table class="order-food-table-qrcode-print__table">
          <tr v-for="rowIdx in 4" :key="rowIdx">
            <td v-for="colIdx in 4" :key="colIdx">
              <template v-if="page[(rowIdx - 1) * 4 + (colIdx - 1)]">
                <div class="order-food-table-qrcode-print__cell">
                  <div class="order-food-table-qrcode-print__logo">
                    <img src="@/assets/img/asset/logo.png" alt="logo">
                  </div>
                  <div class="order-food-table-qrcode-print__qr-box">
                    <vue-qr
                      v-if="page[(rowIdx - 1) * 4 + (colIdx - 1)].shortUrl"
                      :text="page[(rowIdx - 1) * 4 + (colIdx - 1)].shortUrl"
                      :size="140"
                      :margin="0"
                      :correct-level="3"
                      :dot-scale="1"
                    />
                    <div v-else class="order-food-table-qrcode-print__qr-error">
                      {{ page[(rowIdx - 1) * 4 + (colIdx - 1)].errorText || $t('OrderFoodEntry.ConfirmPrintFailed') }}
                    </div>
                  </div>
                  <div class="order-food-table-qrcode-print__text">
                    {{ page[(rowIdx - 1) * 4 + (colIdx - 1)].tableNo }}
                  </div>
                </div>
              </template>
            </td>
          </tr>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { ORDER_FOOD_PRINT_TABLE_LIST } from '@/js/utils/orderFood'

function chunk (arr, size) {
  const out = []
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size))
  return out
}

export default {
  name: 'OrderFoodTableQrCodePrint',
  data () {
    return {
      isLoading: true, // 是否正在載入
      items: [] // 桌號 QRCode 列表
    }
  },
  computed: {
    pages () {
      return chunk(this.items, 16)
    }
  },
  mounted () {
    this.load()
  },
  methods: {
    /**
     * 建立完整 URL
     * @param tableNo {String} 桌號
     * @returns {String} 完整 URL
     */
    buildFullUrl (tableNo) {
      const comId = JSON.parse(localStorage.getItem('loginUserInfo')).ComId
      return `https://access.smartdaily.com.tw/launchApp.html?page=orderFood&param=comId:${comId};tableNo:${tableNo}`
    },
    /**
     * 載入桌號 QRCode
     */
    async load () {
      this.isLoading = true
      this.items = []

      let tableNoList = []
      try {
        tableNoList = JSON.parse(localStorage.getItem(ORDER_FOOD_PRINT_TABLE_LIST) || '[]')
      } catch (e) {
        tableNoList = []
      }

      tableNoList = (Array.isArray(tableNoList) ? tableNoList : [])
        .map(v => String(v || '').trim())
        .filter(Boolean)

      if (tableNoList.length === 0) {
        this.isLoading = false
        return
      }

      // 暫時不走短網址 API，直接使用完整 URL 作為 QRCode 內容
      this.items = tableNoList.map(tableNo => ({
        tableNo,
        fullUrl: this.buildFullUrl(tableNo),
        shortUrl: this.buildFullUrl(tableNo),
        errorText: ''
      }))

      this.isLoading = false

      this.print()
    },
    /**
     * 列印
     */
    print () {
      if (!this.$refs.printPage) return
      setTimeout(() => {
        if (confirm('是否列印此頁？')) {
          this.$print(this.$refs.printPage)
        }
      }, 1000)
    }
  }
}
</script>

<style lang="scss" scoped>
// 版面常數：畫面與列印共用，改一處即可
$of-tqr-a4-width: 210mm;
$of-tqr-a4-min-height: 297mm;
$of-tqr-qr: 140px;
$of-tqr-border-table: #ddd;
$of-tqr-border-page: #d3d3d3;

.order-food-table-qrcode-print {
  &__print {
    display: block;
    margin: 0 auto;
  }

  &__page {
    width: $of-tqr-a4-width;
    min-height: $of-tqr-a4-min-height;
    padding: 0;
    margin: 0 auto;
    border: 1px solid $of-tqr-border-page;
    border-radius: 5px;
    background: #fff;
    box-shadow: 0 0 5px rgba(0, 0, 0, 0.1);
  }

  &__title {
    font-size: map-get($fontSize, xx-large);
    font-weight: bold;
    text-align: left;
    padding: 15px 16px;
  }

  &__table {
    width: 100%;
    border: 1px solid $of-tqr-border-table;
    border-collapse: collapse;
    table-layout: fixed;

    td {
      width: 25%;
      border: 1px solid $of-tqr-border-table;
      text-align: center;
      vertical-align: top;
      position: relative;
    }
  }

  &__cell {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    padding: 24px;
  }

  &__logo {
    width: $of-tqr-qr;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  &__qr-box {
    width: $of-tqr-qr;
    height: $of-tqr-qr;
    margin: 10px auto 16px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__qr-error {
    width: $of-tqr-qr;
    height: $of-tqr-qr;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: map-get($fontSize, small);
    font-weight: 700;
    padding: 8px;
    box-sizing: border-box;
  }

  &__text {
    font-size: map-get($fontSize, large);
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    word-break: break-all;
    padding: 0 8px;
    box-sizing: border-box;
  }

  @media print {
    * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }

    .no-print {
      display: none !important;
    }

    &__print {
      display: block !important;
      color: #000;
    }

    &__page {
      page-break-after: always;
    }
  }
}
</style>

<!-- @page 與 html/body 須非 scoped，否則部分瀏覽器列印不套用 -->
<style lang="scss">
@media print {
  @page {
    margin: 0;
    size: A4;
  }

  html,
  body {
    margin: 0;
    padding: 0;
  }
}
</style>
